import useKlydoScreen from '../hooks/useKlydoScreen';
import DSBox from '../../../design-system/DSBox';
import { PopulatedKlydo } from '../../../api/klydo';
import TimePicker from '../../../components/TimePicker/components/TimePicker';
import KlydoEditableView from './KlydoEditableView';
import KlydoReadOnlyView from './KlydoReadOnlyView';
import DSCircularProgress from '../../../design-system/DSCircularProgress';
import DSTabGradientViewPaper from '../../../design-system/Paper/DSTabGradientViewPaper';
import { DSTitleTypography } from '../../../design-system/Typography';
import useDeviceType from '../../../hooks/useDeviceType';
import useKlydoScale from '../../../hooks/useKlydoScale';
import DSGradientViewPaper from '../../../design-system/Paper/DSGradientViewPaper';
import KlydoPreview from '../../../components/KlydoPlayer/Wrappers/KlydoPreview';
import FirstKlydoDialog from './FirstKlydoDialog';

const KlydoScreen = () => {
  const {
    klydoSecureUrl,
    minutesValue,
    hoursValue,
    isDialsHidden,
    spacing,
    menuButtonItemsList,
    isTimpePickerOpen,
    handleClosedTimePicker,
    klydo,
    isEditableView,
    isEditingState,
    handleKlydoThemeChange,
    setSuggestedColors,
    selectedTheme,
    suggestedColors,
    handleLocalUrlChange,
    handleSecuredUrlChange,
    klydoId,
    handleBackToEditClick,
    isLoading,
    handleSubmitKlydo,
    handleKeepEditorMounted,
    isAnimationPaused,
    collectionLogo,
    collectionPendulumLogoSize,
    collectionPendulumLogoType,
    isApprovedCollection,
    previewKlydo,
    isMuted,
    isFirstKlydoDialogOpen,
    handleCloseFirstKlydoDialog,
  } = useKlydoScreen();

  const { isMobile } = useDeviceType();
  const dynamicScale = useKlydoScale();

  if (isLoading) {
    return (
      <DSTabGradientViewPaper
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'column',
          paddingY: spacing(4),
          height: '87vh',
          gap: spacing(3),
        }}
      >
        <DSCircularProgress />
      </DSTabGradientViewPaper>
    );
  }

  if (isMobile) {
    return (
      <DSTabGradientViewPaper
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '87vh',
          padding: spacing(3),
        }}
      >
        <DSTitleTypography color="error" sx={{ textAlign: 'center' }}>
          For the best experience creating and editing klydos requires a tablet
          or desktop.
        </DSTitleTypography>
      </DSTabGradientViewPaper>
    );
  }

  return (
    <>
      <DSBox
        sx={{
          flexDirection: { xxs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          display: 'flex',
          width: '98%',
          gap: spacing(3),
          height: '87vh',
        }}
      >
        <DSGradientViewPaper
          sx={{
            display: 'flex',
            justifyContent: 'flex-start',
            flexDirection: 'column',
            flexGrow: 1,
            width: { xxs: '100%', xs: '60%', md: '60%', xl: '69%' },
            paddingX: { xs: spacing(4), sm: spacing(6), md: spacing(8) },
            paddingY: spacing(4),
            height: '100%',
            minHeight: 0,
          }}
        >
          {isEditableView ? (
            <KlydoEditableView
              klydo={klydo as PopulatedKlydo}
              setSuggestedColors={setSuggestedColors}
              onKlydoThemeChange={handleKlydoThemeChange}
              suggestedColors={suggestedColors}
              onLocalUrlChange={handleLocalUrlChange}
              onSecuredUrlChange={handleSecuredUrlChange}
              klydoSecureUrl={klydoSecureUrl || ''}
              selectedTheme={selectedTheme}
              klydoId={klydoId}
              onSubmitKlydo={handleSubmitKlydo}
              isEditingState={isEditingState}
              onKeepEditorMounted={handleKeepEditorMounted}
            />
          ) : (
            klydo && (
              <KlydoReadOnlyView
                klydo={klydo}
                onClickEdit={handleBackToEditClick}
              />
            )
          )}
        </DSGradientViewPaper>

        <KlydoPreview
          klydo={previewKlydo}
          minutesValue={minutesValue}
          hoursValue={hoursValue}
          menuButtonItemsList={menuButtonItemsList}
          dialsHidden={isDialsHidden}
          scale={dynamicScale}
          isPaused={isEditableView && isAnimationPaused}
          pendulumLogo={collectionLogo}
          pendulumLogoSize={collectionPendulumLogoSize ?? undefined}
          isApprovedCollection={isApprovedCollection}
          pendulumLogoType={collectionPendulumLogoType ?? undefined}
          muted={isMuted}
        />
      </DSBox>

      <TimePicker isOpen={isTimpePickerOpen} onClose={handleClosedTimePicker} />

      <FirstKlydoDialog
        open={isFirstKlydoDialogOpen}
        onClose={handleCloseFirstKlydoDialog}
      />
    </>
  );
};

export default KlydoScreen;
